import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { date: "", name: "", type: "National", reason: "", location_ids: [], half_day: false, working_hours: 4 };

export default function HolidayAdmin() {
  const qc = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [form, setForm] = useState(EMPTY);

  const { data: years } = useQuery({ queryKey: ["holidayYears"], queryFn: async () => (await api.get("/holidays/years")).data });
  const { data, isLoading } = useQuery({ queryKey: ["holidays", year], queryFn: async () => (await api.get("/holidays", { params: { year } })).data });

  const { data: locations } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/attendance/locations")).data });
  const toggleLoc = (id) => setForm((f) => ({ ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id] }));
  const isClosure = form.type === "Site Closure";

  const yearOptions = Array.from(new Set([...(years || []), thisYear, thisYear + 1])).sort((a, b) => b - a);

  const add = useMutation({
    mutationFn: async () => (await api.post("/holidays", { year, ...form })).data,
    onSuccess: (res) => { toast.success(res?.notified ? `Holiday added. ${res.notified} staff notified.` : "Holiday added"); (res?.warnings || []).forEach((w) => toast.warning(w, { duration: 15000 })); qc.invalidateQueries({ queryKey: ["holidays", year] }); qc.invalidateQueries({ queryKey: ["holidayYears"] }); setForm(EMPTY); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/holidays/${id}`)).data,
    onSuccess: (res) => { toast.success("Holiday removed"); (res?.warnings || []).forEach((w) => toast.warning(w, { duration: 15000 })); qc.invalidateQueries({ queryKey: ["holidays", year] }); },
  });

  return (
    <div>
      <PageHeader overline="Company" title="Holiday Calendar Admin" icon={CalendarDays}
        subtitle="Publish the year-specific holiday list. Employees see this in their portal."
        actions={
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32 bg-white" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        } />

      <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-4 gap-3 items-end mb-4">
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="holiday-date" /></div>
        <div className="sm:col-span-1"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="holiday-name" /></div>
        <div><Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{["National", "Restricted", "Weekly Off", "Site Closure"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {form.type !== "Restricted" && (
          <div className="sm:col-span-4 flex flex-wrap items-center gap-4" data-testid="half-day-options">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} data-testid="holiday-half-day" />
              Half-day holiday (e.g. eve of Diwali, election day, New Year's Eve)
            </label>
            {form.half_day && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                Staff work
                <Input type="number" min="1" max="8" step="0.5" value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: Number(e.target.value) })} className="w-20" data-testid="holiday-working-hours" />
                hours that day. Leaving after that is not early-leave and not a Half Day.
              </label>
            )}
          </div>
        )}
        <Button className="bg-navy hover:bg-navy-700" disabled={!form.date || !form.name || add.isPending || (isClosure && form.location_ids.length === 0)} onClick={() => add.mutate()} data-testid="add-holiday"><Plus className="h-4 w-4 mr-1" /> Publish</Button>
        {isClosure && (
          <div className="sm:col-span-4 border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-500 mb-2">Forced closure (fire, flood, curfew, state-declared holiday...). Only staff working at the selected locations get this day off, with no leave or salary deduction. Everyone else works as normal.</p>
            <Label>Reason</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 mb-3" placeholder="e.g. Fire at the centre, closed for one day" data-testid="holiday-reason" />
            <Label>Closed locations</Label>
            <div className="mt-1 grid sm:grid-cols-3 gap-1 max-h-48 overflow-y-auto" data-testid="closure-locations">
              {(locations || []).map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} /> {l.name}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">Add one entry per closed date for a multi-day closure.</p>
          </div>
        )}
      </Card>

      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={CalendarDays} title={`No holidays for ${year}`} hint="Add holidays above to publish the calendar for this year." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Date</TableHead>
              <TableHead className="overline text-slate-500">Name</TableHead>
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((h) => (
                <TableRow key={h.id} className="table-row-i" data-testid={`holiday-${h.id}`}>
                  <TableCell className="font-mono-i text-sm text-teal">{h.date}</TableCell>
                  <TableCell className="font-medium text-navy">{h.name}</TableCell>
                  <TableCell className="text-slate-600">{h.type}{h.half_day && <span className="ml-2 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5">Half day · {h.working_hours}h</span>}{h.location_names?.length > 0 && <span className="block text-xs text-red-600">{h.location_names.join(", ")}{h.reason ? ` · ${h.reason}` : ""}</span>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-red-500" onClick={() => del.mutate(h.id)} data-testid={`del-holiday-${h.id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
